'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
} from './combobox';

// ---------------------------------------------------------------------------
// AsyncSelect — typeahead single-select backed by a remote search, ported
// from the site-builder blocks package's `SbAsyncSelect`
// (`_shared/SbAsyncSelect.tsx`) onto this package's `Combobox` primitives
// (`combobox.tsx`, `@base-ui/react`-based). `filter={null}` + externally
// managed `items` disables the primitive's own client-side filtering since
// `fetchOptions` already does the (server-side) filtering.
// ---------------------------------------------------------------------------

export interface AsyncSelectOption {
  value: string;
  label: string;
}

export interface AsyncSelectProps {
  /** Current selection ({value,label}) or null when empty. */
  value: AsyncSelectOption | null;
  onChange: (value: AsyncSelectOption | null) => void;
  /** Fetch matching options for a (possibly empty) search term. */
  fetchOptions: (term: string, signal: AbortSignal) => Promise<AsyncSelectOption[]>;
  /** Re-fetch when this key changes (e.g. a parent cascade id). */
  reloadKey?: string;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  allowClear?: boolean;
  /** Invalid (failed-validation) visual state. */
  invalid?: boolean;
  noResultText?: string;
  loadingText?: string;
  className?: string;
}

const SEARCH_DEBOUNCE = 400;

export function AsyncSelect({
  value,
  onChange,
  fetchOptions,
  reloadKey,
  placeholder,
  ariaLabel,
  disabled = false,
  allowClear = true,
  invalid = false,
  noResultText,
  loadingText,
  className,
}: AsyncSelectProps) {
  const { t } = useTranslation('ui');
  const [open, setOpen] = React.useState(false);
  const [term, setTerm] = React.useState(value?.label ?? '');
  const [options, setOptions] = React.useState<AsyncSelectOption[]>([]);
  const [loading, setLoading] = React.useState(false);

  // `term` is fully local state (it also drives the debounced remote
  // search below, independent of `value`), so it only ever gets a fresh
  // label through the selection/clear callbacks that flow through this
  // component. It never learns about a `value` set from *outside* — e.g.
  // an async prefill landing after mount — so without this, a
  // programmatically-set value shows its clear button (a real selection)
  // over permanently blank input text. Only resync while closed so an
  // in-progress search isn't clobbered by an unrelated external update
  // (e.g. a sibling cascade field resetting this one to null).
  React.useEffect(() => {
    if (open) return;
    setTerm(value?.label ?? '');
  }, [value?.value, value?.label, open]);

  // Debounced remote fetch while open. Resets when the parent cascade
  // (reloadKey) changes so stale child options never linger.
  React.useEffect(() => {
    if (!open) return;
    const ctrl = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      fetchOptions(term, ctrl.signal)
        .then((opts) => {
          if (ctrl.signal.aborted) return;
          setOptions(opts);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (ctrl.signal.aborted) return;
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setOptions([]);
          setLoading(false);
        });
    }, SEARCH_DEBOUNCE);
    return () => {
      ctrl.abort();
      clearTimeout(timer);
    };
  }, [open, term, reloadKey, fetchOptions]);

  return (
    <Combobox
      items={options}
      filter={null}
      value={value}
      onValueChange={(next) => onChange(next as AsyncSelectOption | null)}
      inputValue={term}
      onInputValueChange={setTerm}
      open={open}
      onOpenChange={setOpen}
      disabled={disabled}
    >
      <ComboboxInput
        placeholder={placeholder ?? t('common:placeholders.select')}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        showClear={allowClear}
        className={className}
      />
      <ComboboxContent>
        <ComboboxList>
          {loading ? (
            <div className="px-2 py-2 text-center text-sm text-muted-foreground">
              {loadingText ?? t('common:messages.loading')}
            </div>
          ) : (
            <>
              <ComboboxEmpty>{noResultText ?? t('common:messages.noResults')}</ComboboxEmpty>
              {options.map((opt) => (
                <ComboboxItem key={opt.value} value={opt}>
                  {opt.label}
                </ComboboxItem>
              ))}
            </>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
